import { useContext, useMemo, useRef, useState } from 'react';

import { ColDef, RowDoubleClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Toolbar } from 'primereact/toolbar';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { addToast } from '@/app/slices/toast';
import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, sideBar } from '@/grid/defaults';
import { PrimaryContext } from '@/services';
import { useSemenTypes } from '@/services/lookups/hooks';
import type { StrippingFertilizationResponse } from '@/services/stripping/types';
import { useContextMenu } from '@/utils/context-menu';

import { useStrippingFertilizations } from '../../services/stripping/hooks';
import { getColumnDefs } from './columnDefs';
import { useFertilizationDate, useQcLotNumber } from './hooks';
import { QCDialog } from './QCDialog';
import { StrippingForm } from './StrippingForm';
import { StrippingProgress } from './StrippingProgress';
import { StrippingSelector } from './StrippingSelector';
import StrippingContext from './useStrippingContext';

import './QCDialog.css';
import './Stripping.css';

export const StrippingPage = () => {
  const defaultGridOptions = useDefaultGridOptions();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { themeMode } = useContext(PrimaryContext);
  const [params] = useSearchParams();
  const gridRef = useRef<AgGridReact>(null);

  const fertilizationDate = useFertilizationDate();
  const fertilizationSiteId = params.get('site');
  const strippingTechnicianId = params.get('tech');

  const [qcStripperTipLotNumber, setQcStripperTipLotNumber] = useQcLotNumber(
    fertilizationDate,
    fertilizationSiteId,
  );
  const [selectedRow, setSelectedRow] =
    useState<StrippingFertilizationResponse>();
  const [quickSearchValue, setQuickSearchValue] = useState('');
  const [editStrippingDialogVisible, setEditStrippingDialogVisible] =
    useState(false);
  const [qcDialogVisible, setQCDialogVisible] = useState(false);

  const semenTypes = useSemenTypes();

  const { data: fertilizations, refetch: refetchFertilizations } =
    useStrippingFertilizations({
      fertilizationDate,
      fertilizationSiteId,
      strippingTechnicianId,
    });

  const value = useMemo(
    () => ({
      gridRef,
      selectedRow,
      setSelectedRow,
      refetchFertilizations,
      qcStripperTipLotNumber,
      setQcStripperTipLotNumber,
    }),
    [
      selectedRow,
      refetchFertilizations,
      qcStripperTipLotNumber,
      setQcStripperTipLotNumber,
    ],
  );

  const duplicateDonorIds = useMemo(() => {
    if (!fertilizations) {
      return;
    }
    const donorIds = fertilizations.map((row) => row?.donorSchedule?.donor?.id);
    const duplicateDonorIds: string[] = [];
    for (let i = 0; i < donorIds.length; i++) {
      const donorId = donorIds[i];
      if (donorId && donorIds.indexOf(donorId) !== i) {
        duplicateDonorIds.push(donorId);
      }
    }
    return duplicateDonorIds;
  }, [fertilizations]);

  const handleRowDoubleClicked = (e: RowDoubleClickedEvent) => {
    if (
      qcStripperTipLotNumber === '' ||
      qcStripperTipLotNumber === null ||
      qcStripperTipLotNumber === undefined
    ) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: t('common.warning'),
          detail: t('qc.pleaseFillinQcInformation'),
          life: 3000,
        }),
      );
      return;
    }
    setSelectedRow(e.data);
    setEditStrippingDialogVisible(true);
  };

  const colDefs = useMemo<ColDef<StrippingFertilizationResponse>[]>(() => {
    return getColumnDefs({
      semenTypeData: semenTypes,
      fertilizationDate,
    });
  }, [semenTypes, fertilizationDate]);

  const startContent = <StrippingSelector />;

  const endContent = (
    <div className="stripping__progress">
      <Button
        label={t('qc.qc')}
        icon="pi pi-eye"
        onClick={() => setQCDialogVisible(true)}
        disabled={!fertilizations?.length}
      />
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search"> </InputIcon>
        <InputText
          placeholder={t('common.search')}
          value={quickSearchValue}
          onChange={(e) => setQuickSearchValue(e.target.value)}
        />
      </IconField>

      {fertilizations && (
        <StrippingProgress
          total={fertilizations?.length as number}
          completed={
            fertilizations?.filter((item) => item.strippedAt).length as number
          }
        ></StrippingProgress>
      )}
    </div>
  );

  const { getContextMenuItems } = useContextMenu({ idKey: 'id' });

  return (
    <div className="common-page stripping">
      <StrippingContext.Provider value={value}>
        <Toolbar
          className="toolbar-above-panel"
          start={startContent}
          end={endContent}
        />
        <div className="stripping__grid">
          <WithLocalStorage storageKey="stripping-grid">
            <AgGridReact
              gridOptions={defaultGridOptions}
              className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}`}
              rowData={fertilizations}
              defaultColDef={defaultColDef}
              columnDefs={colDefs}
              context={duplicateDonorIds}
              overlayLoadingTemplate={t(
                'stripping.pleaseSelectSiteAndTechnician',
              )}
              rowSelection="single"
              ref={gridRef}
              onRowDoubleClicked={handleRowDoubleClicked}
              getRowId={(params) => params.data.id}
              quickFilterText={quickSearchValue}
              sideBar={sideBar}
              getContextMenuItems={getContextMenuItems}
            />
          </WithLocalStorage>
          {editStrippingDialogVisible && (
            <StrippingForm
              visible={editStrippingDialogVisible}
              setVisible={setEditStrippingDialogVisible}
            ></StrippingForm>
          )}
          {qcDialogVisible && (
            <QCDialog
              visible={qcDialogVisible}
              setVisible={setQCDialogVisible}
            />
          )}
        </div>
      </StrippingContext.Provider>
    </div>
  );
};
